import { styled } from "goober";
import {
  Fragment,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { queries } from "../../shared/queries";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { useQuery } from "@rocicorp/zero/react";
import { useRecordNavigationClick } from "../hooks/useRecordNavigationClick";
import { type AttentionItem, buildAttentionQueue } from "../lib/attentionQueue";
import { useFormatRelativeTime } from "../hooks/useFormatRelativeTime";
import { isEditableTarget } from "../lib/isEditableTarget";
import { playChatNeedsInputMuffled } from "../lib/sounds";
import { navigate, routes, useMakeUrl, useRouteMatch } from "../lib/urls";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { CircleMesh } from "./CircleMesh";
import { parseBadgeConfig } from "./CircleMeshEditor";
import { SectionLabel } from "./SearchableDropdown";
import { Icon } from "./Icon";
import { Link } from "./Link";
import { pickChatIconColor } from "./PersonalChats/pickChatIconColor";
import { PressButton } from "./PressButton";
import { ShortcutHint } from "./ShortcutHint";
import { StatusIndicator } from "./StatusIndicator";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const Container = styled("div")`
  position: relative;
`;

const TriggerButton = styled(PressButton)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  &:hover {
    background: var(--alpha4);
  }
`;

const MenuPopover = styled(AnchoredPopover)`
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  z-index: ${zIndexes.dropdown};
  width: 280px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Results = styled("div")`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 4px;
`;

const SectionGap = styled("div")`
  height: 8px;
`;

const sectionLabels: Record<AttentionItem["status"], string> = {
  waiting: "Needs you",
  ready: "Ready",
  building: "In progress",
  thinking: "In progress",
};

const rowChrome = `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
`;

const ItemRow = styled(Link)`
  ${rowChrome}
  cursor: pointer;
  color: var(--text-primary);
  text-decoration: none;

  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

const BadgeSlot = styled("div")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkflowBadgeThumb = styled("div")`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  overflow: hidden;
`;

const TitleBlock = styled("div")`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

function itemUrl(
  item: AttentionItem,
  makeUrl: ReturnType<typeof useMakeUrl>
): string {
  if (item.kind === "branch") {
    return makeUrl(routes.space.workflow, {
      spaceId: item.spaceId,
      workflowId: item.workflowId,
      branchId: item.branchId,
    });
  }
  return makeUrl(routes.chats, { tabId: item.personalChatId });
}

function jumpToItem(item: AttentionItem) {
  if (item.kind === "branch") {
    navigate(routes.space.workflow, {
      spaceId: item.spaceId,
      workflowId: item.workflowId,
      branchId: item.branchId,
    });
  } else {
    navigate(routes.chats, { tabId: item.personalChatId });
  }
}

function ItemBadge({ item }: { item: AttentionItem }) {
  if (item.kind === "personalChat") {
    return (
      <BadgeSlot>
        <Icon
          name="ask"
          size={16}
          color={`var(--${pickChatIconColor(item.personalChatId)}-600)`}
        />
      </BadgeSlot>
    );
  }
  const badgeConfig = parseBadgeConfig(item.badgeSeed, item.workflowId);
  return (
    <BadgeSlot>
      <WorkflowBadgeThumb>
        <CircleMesh
          key={item.badgeSeed}
          targetCircleSize={8}
          seed={badgeConfig.seed}
          mutingIntensity={0}
          animate={false}
          restrictedColors={badgeConfig.colors}
          grayscale={item.pausedAt != null}
        />
      </WorkflowBadgeThumb>
    </BadgeSlot>
  );
}

export function AttentionStack() {
  const makeUrl = useMakeUrl();
  const recordClick = useRecordNavigationClick();
  const formatRelativeTime = useFormatRelativeTime();
  const [branches, branchesResult] = useQuery(queries.navBranches());
  const [chatTabs, chatTabsResult] = useQuery(queries.personalChatTabs());
  const [views, viewsResult] = useQuery(queries.userWorkflowViews());

  const [, workflowParams] = useRouteMatch(routes.space.workflow);
  const [, personalWorkflowParams] = useRouteMatch(routes.personal.workflow);
  const [, chatsParams] = useRouteMatch(routes.chats);
  const currentBranchId =
    workflowParams.branchId || personalWorkflowParams.branchId || undefined;
  const currentWorkflowId =
    workflowParams.workflowId || personalWorkflowParams.workflowId || undefined;
  const currentChatTabId = chatsParams.tabId || undefined;

  const lastSeenByWorkflowId = useMemo(
    () => new Map(views.map((v) => [v.workflowId, v.lastSeenAt])),
    [views]
  );

  const items = useMemo(
    () =>
      buildAttentionQueue({
        branches,
        lastSeenByWorkflowId,
        chatTabs,
        currentBranchId,
        currentWorkflowId,
        currentChatTabId,
      }),
    [
      branches,
      lastSeenByWorkflowId,
      chatTabs,
      currentBranchId,
      currentWorkflowId,
      currentChatTabId,
    ]
  );

  // The queue omits the branch/chat you're currently on, so the avatar sleeps
  // only when there's no activity anywhere — including the one you're viewing.
  const hasActivity = useMemo(
    () =>
      buildAttentionQueue({ branches, lastSeenByWorkflowId, chatTabs }).length >
      0,
    [branches, lastSeenByWorkflowId, chatTabs]
  );

  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const resultsRef = useRef<HTMLDivElement | null>(null);

  const toggleOpen = () => {
    if (items.length === 0) return;
    if (!open) recordClick("top_bar", "activity");
    setOpen(!open);
    if (!open) setHighlight(0);
  };

  const close = useCallback(() => setOpen(false), []);
  const [containerRef, portalRef] = useMouseDownOutside(close);

  if (
    highlight >= items.length &&
    highlight !== Math.max(items.length - 1, 0)
  ) {
    setHighlight(Math.max(items.length - 1, 0));
  }
  if (items.length === 0 && open) setOpen(false);

  useEffect(() => {
    if (!open) return;
    resultsRef.current
      ?.querySelector('[data-highlighted="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  const queriesComplete =
    branchesResult.type === "complete" &&
    chatTabsResult.type === "complete" &&
    viewsResult.type === "complete";
  const actionableKeys = useMemo(
    () =>
      new Set(
        items
          .filter((i) => i.status === "waiting" || i.status === "ready")
          .map((i) => i.key)
      ),
    [items]
  );
  const previousActionableRef = useRef<ReadonlySet<string> | null>(null);
  // A key re-enters actionableKeys whenever an item flips back to waiting/ready
  // (e.g. a branch toggling building↔waiting), and several items can become
  // actionable across rapid query updates — each reads as "new". Throttle so
  // those don't stack into overlapping muffled pings.
  const lastMuffledPingRef = useRef(0);
  useEffect(() => {
    if (!queriesComplete) return;
    const previous = previousActionableRef.current;
    previousActionableRef.current = actionableKeys;
    if (previous === null) return;
    for (const key of actionableKeys) {
      if (!previous.has(key)) {
        const now = Date.now();
        if (now - lastMuffledPingRef.current > 1000) {
          playChatNeedsInputMuffled();
          lastMuffledPingRef.current = now;
        }
        break;
      }
    }
  }, [queriesComplete, actionableKeys]);

  const handleStackKey = useEffectEvent((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key === "j") {
      e.preventDefault();
      e.stopPropagation();
      toggleOpen();
      return;
    }
    if (!open) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
      return;
    }
    const count = items.length;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      if (count === 0) return;
      const delta = e.key === "ArrowDown" ? 1 : -1;
      setHighlight((was) => (was + delta + count) % count);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const item = items[highlight];
      if (!item) return;
      setOpen(false);
      recordClick("top_bar", "activity_item");
      jumpToItem(item);
      return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (isEditableTarget(e)) return;
    if (e.key >= "1" && e.key <= "9") {
      const item = items[Number(e.key) - 1];
      if (!item) return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      recordClick("top_bar", "activity_item");
      jumpToItem(item);
    }
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => handleStackKey(e);
    // Capture phase so an open menu owns its keys outright — handlers on the
    // page beneath (composer, lists, step map) never see them.
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  // Closing rides the Link's own mousedown (which navigates synchronously
  // right after this runs): closing on pointerdown would unmount the row
  // before its mousedown navigation ever fired.
  const handleItemMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey) return;
      recordClick("top_bar", "activity_item");
      setOpen(false);
    },
    [recordClick]
  );

  const topActionable = items.find(
    (i) => i.status === "waiting" || i.status === "ready"
  );
  const asleep = !hasActivity;
  const canOpen = items.length > 0;

  return (
    <Container ref={containerRef}>
      <MenuPopover
        isOpen={open}
        popoverRef={portalRef}
        direction="down"
        style={
          {
            margin: "8px 0 0",
            top: "anchor(bottom)",
            // Nudged right so the row status indicators (12px from the menu
            // edge) line up with the trigger's indicator (8px from its edge).
            right: "calc(anchor(right) - 4px)",
            positionTryFallbacks: "flip-block",
          } as React.CSSProperties
        }
        trigger={(props) => (
          <div {...props}>
            <Tooltip
              hidden={open}
              content={
                asleep ? (
                  <>No 3B activity</>
                ) : canOpen ? (
                  <>
                    3B activity <ShortcutHint>⌘J</ShortcutHint>
                  </>
                ) : (
                  <>3B activity</>
                )
              }
            >
              <TriggerButton
                aria-label={asleep ? "No 3B activity" : "3B activity"}
                aria-expanded={open}
                onPress={toggleOpen}
                onMouseDown={(e) => e.preventDefault()}
                style={canOpen ? undefined : { cursor: "default" }}
              >
                <StatusIndicator
                  state={
                    asleep ? "sleeping" : (topActionable?.status ?? "resting")
                  }
                  focused={topActionable != null}
                />
              </TriggerButton>
            </Tooltip>
          </div>
        )}
      >
        <Results ref={resultsRef}>
          {items.map((item, index) => {
            const section = sectionLabels[item.status];
            const showHeader =
              index === 0 ||
              section !== sectionLabels[items[index - 1]!.status];
            return (
              <Fragment key={item.key}>
                {showHeader && (
                  <>
                    {index > 0 && <SectionGap />}
                    <SectionLabel>
                      <Text variant="text-small" color="var(--text-secondary)">
                        {section}
                      </Text>
                    </SectionLabel>
                  </>
                )}
                <ItemRow
                  to={itemUrl(item, makeUrl)}
                  data-highlighted={index === highlight}
                  onMouseEnter={() => setHighlight(index)}
                  onMouseDown={handleItemMouseDown}
                >
                  <ItemBadge item={item} />
                  <TitleBlock>
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {item.name}
                    </Text>
                    {item.since > 0 && (
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {formatRelativeTime(item.since)}
                      </Text>
                    )}
                  </TitleBlock>
                  <StatusIndicator state={item.status} focused />
                </ItemRow>
              </Fragment>
            );
          })}
        </Results>
      </MenuPopover>
    </Container>
  );
}
